"use client";

import * as Sentry from "@sentry/nextjs";
import { usePathname } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useAuth, useUser } from "@/auth/hooks/clerk-hooks";

type TrackingAuth = ReturnType<typeof useAuth> & {
	user?: {
		id?: number | string | null;
		clerk?: { id?: string | null } | null;
	} | null;
	sessionClaims?: {
		userId?: number | string | null;
	} | null;
};

type TrackingUser = {
	firstName?: string | null;
	lastName?: string | null;
	primaryEmailAddress?: { emailAddress?: string | null } | null;
	emailAddresses?: Array<{ emailAddress?: string | null }>;
} | null;

function normalizeNumericId(value: unknown): string | null {
	if (value === null || value === undefined || value === "") return null;
	const id = String(value);
	return /^\d+$/.test(id) ? id : null;
}

function getUserEmail(user: TrackingUser): string | undefined {
	return user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? undefined;
}

export default function PostHogPageView(): null {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const posthog = usePostHog();

	const auth = useAuth() as TrackingAuth;
	const { isLoaded: isAuthLoaded, isSignedIn, sessionClaims, userId: clerkUserId } = auth;
	const { isLoaded: isUserLoaded, user } = useUser() as ReturnType<typeof useUser> & { user: TrackingUser };
	const claimedUserId = sessionClaims?.userId ? String(sessionClaims.userId) : null;
	const user_id = normalizeNumericId(auth.user?.id) ?? normalizeNumericId(claimedUserId);
	const resolved_clerk_user_id = auth.user?.clerk?.id ?? clerkUserId ?? null;
	const posthogDistinctId = user_id ?? claimedUserId ?? resolved_clerk_user_id ?? null;
	const userEmail = getUserEmail(user);
	const isReadyForTracking = isAuthLoaded && (!isSignedIn || !!posthogDistinctId);
	const lastIdentifiedIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (posthogDistinctId) {
			lastIdentifiedIdRef.current = posthogDistinctId;
			Sentry.setUser({
				id: posthogDistinctId,
				email: userEmail,
				ip_address: "{{auto}}"
			});

			posthog.identify(posthogDistinctId, {
				email: userEmail,
				user_id,
				clerk_user_id: resolved_clerk_user_id,
				firstName: user?.firstName,
				lastName: user?.lastName,
			});
		} else if (isAuthLoaded && isSignedIn === false) {
			Sentry.setUser(null);
			if (lastIdentifiedIdRef.current) {
				posthog.reset();
				lastIdentifiedIdRef.current = null;
			}
		}
	}, [
		clerkUserId,
		isAuthLoaded,
		isSignedIn,
		posthog,
		posthogDistinctId,
		resolved_clerk_user_id,
		user_id,
		user?.firstName,
		user?.lastName,
		userEmail,
	]);

	useEffect(() => {
		if (!pathname || !posthog || !isReadyForTracking) return;
		if (isSignedIn && !isUserLoaded) return;

		let url = window.origin + pathname;
		if (searchParams.toString()) url = url + `?${searchParams.toString()}`;

		posthog.capture("$pageview", {
			$current_url: url,
			user_id,
			clerk_user_id: resolved_clerk_user_id,
			user_email: userEmail,
		});
	}, [
		clerkUserId,
		isReadyForTracking,
		isSignedIn,
		isUserLoaded,
		pathname,
		posthog,
		resolved_clerk_user_id,
		searchParams,
		user_id,
		userEmail,
	]);

	return null;
}
